import React from 'react';
import { TextInput, TextInputProps } from 'grommet';
import { observer } from 'mobx-react-lite';
import { Box, Text } from '/src/components';
import {
  DataTableCellUnit,
  FloatString,
  MeteredEquipmentUsageDataForm,
  Nullable,
  UnmeteredEquipmentUsageDataForm,
} from '/src/lib/types';
import { formatNumber as formatNumberUtil } from '/src/utils';

export type UsagesDataTableCellProps = {
  index: number;
  width: string | number;
  isEditable?: boolean;
  valueKey?: string;
  formValues?: UnmeteredEquipmentUsageDataForm | MeteredEquipmentUsageDataForm;
  updateFormValue?: (key: string, value: string) => void;
  formErrors?: Record<string, string>;
  inputType?: TextInputProps['type'];
  inputMode?: TextInputProps['inputMode'];
  unit?: DataTableCellUnit;
  formatNumber?: boolean;
  background?: string;
};

export const UsagesDataTableCell: React.FC<UsagesDataTableCellProps> = observer((props) => {
  const {
    index,
    width,
    isEditable,
    valueKey,
    formValues,
    updateFormValue,
    formErrors,
    inputType = 'number',
    inputMode = 'decimal',
    unit,
    formatNumber,
    background,
  } = props;

  const value =
    valueKey && (typeof formValues?.[valueKey] === 'string' || typeof formValues?.[valueKey] === 'number')
      ? formValues[valueKey]
      : '-';

  const canEdit = isEditable && !!valueKey && !!formValues && !!updateFormValue;
  const error = valueKey ? formErrors?.[valueKey] : undefined;

  return (
    <Box
      key={index}
      width={typeof width === 'number' ? width.toString() : width}
      align="center"
      justify="start"
      border={{ side: 'all', size: '0.4px', color: 'light-2' }}
      pad={{ top: '26px', horizontal: '17px' }}
      gap="xsmall"
      direction="column"
      background={background}
      style={{ minHeight: '96px', position: 'relative' }}
    >
      <Box row gap="xsmall" align="center" justify="center" width="100%" flex={false}>
        {!canEdit && (
          <Box height="2.25rem" justify="center">
            <Text toUpperCase>{formatNumber ? formatNumberUtil(value, true) || '-' : value}</Text>
          </Box>
        )}
        {canEdit && (
          <Box flex="grow" align="center">
            <TextInput
              id={valueKey}
              name={valueKey}
              value={(formValues[valueKey] as Nullable<FloatString>) ?? ''}
              type={inputType}
              inputMode={inputMode}
              textAlign="center"
              onChange={(e) => (valueKey ? updateFormValue(valueKey, e.target.value) : undefined)}
              className="hide-input-arrows"
              height="2.25rem"
              style={{
                background: !canEdit ? 'light-2' : 'white',
                paddingTop: '8px',
                paddingBottom: '8px',
                borderColor: error ? 'red' : undefined,
              }}
            />
          </Box>
        )}
        {unit && (
          <Text>
            {unit === 'percent' && '%'}
            {unit === 'kwh' && 'kWh'}
          </Text>
        )}
      </Box>
      {error && (
        <Box width="100%" flex={false}>
          <Text
            size="0.7rem"
            color="status-error"
            style={{
              textAlign: 'center',
              lineHeight: '1',
              display: 'block',
            }}
          >
            {error}
          </Text>
        </Box>
      )}
    </Box>
  );
});
